import {useQuantityInBasketBySizeId} from '@aboutyou/user';
import {useCallback} from 'react';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {useSizesSection} from '../../useGetProductBulkSection';
import {isAddToBasketDisabledForSize} from '../isAddToBasketDisabledForSize';

interface UseAddToBasketRoundCtaActionParams {
  sizesSection: ReturnType<typeof useSizesSection>['data'];
  sizeSelectionOpenedStateKey?: SizeSelectionOpenedStateKey;
}

/**
 * Round-CTA click handler for the article-detail page. Every add is
 * delegated to the size selection opened state: multi-size products get the
 * size flyout, one-size products are auto-added by AddToBasketFlyout on
 * mount, which also owns captcha handling and success/error feedback.
 * The round CTA never renders a disabled state: when the main CTA's disabled
 * rule applies to a one-size product (sold out or the basket already holds
 * all stock), the round CTA is hidden entirely (isHidden). Sold-out is known
 * with the sizes data; the basket cap can only be evaluated after the first
 * client-side basket fetch, until then the CTA stays visible and clicking it
 * opens the flyout.
 * For one-size products the opened state stays set exactly while the
 * auto-add is unresolved (released on success, non-captcha error, or captcha
 * close), so it doubles as the CTA's loading signal (isLoading). Multi-size
 * products open the flyout synchronously and never report loading.
 */
export const useAddToBasketRoundCtaAction = ({
  sizesSection,
  sizeSelectionOpenedStateKey = SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION,
}: UseAddToBasketRoundCtaActionParams) => {
  const {open: openSizeSelection, isOpen: isSizeSelectionOpen} =
    useSizeSelectionOpenedState(sizeSelectionOpenedStateKey);

  const oneSizeSize =
    sizesSection?.sizeSelection.type?.$case === 'oneSize'
      ? sizesSection.sizeSelection.type.oneSize.size
      : undefined;
  const quantityInBasket = useQuantityInBasketBySizeId(oneSizeSize?.sizeId);

  const isHidden = isAddToBasketDisabledForSize(oneSizeSize, quantityInBasket);
  const isLoading = !!oneSizeSize && isSizeSelectionOpen;

  const handleClick = useCallback(() => {
    if (isHidden || isLoading) {
      return;
    }

    openSizeSelection();
  }, [isHidden, isLoading, openSizeSelection]);

  return {handleClick, isHidden, isLoading};
};
